<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
import TrendDown from '@/assets/icon/down.svg'
import { useEcharts } from '~/composables/useEcharts'
import BnbIcon from '@/assets/img/exchange-detail/exchange-funds/bnb.png'
import BtcIcon from '@/assets/img/exchange-detail/exchange-funds/btc.png'
import EthIcon from '@/assets/img/exchange-detail/exchange-funds/eth.png'
import OtherIcon from '@/assets/img/exchange-detail/exchange-funds/other.svg?url'
import UsdtIcon from '@/assets/img/exchange-detail/exchange-funds/usdt.png'
import type { TraderDetailAssetStrength } from '~/api/trader-detail'

type ChartTooltipType = 'flow' | 'volume'

type ChartTooltipState = {
  visible: boolean
  type: ChartTooltipType
  index: number
  left: number
  top: number
  time: string
  value: string
  label: string
}

type ReserveLabel = {
  icon: string
  label: string
  className: string
  name: string
  amount: string
  color: string
  value: number
}

type ReserveChartData = ReserveLabel & {
  itemStyle: {
    color: string
    borderColor: string
    borderWidth: number
  }
  labelLine: {
    lineStyle: {
      color: string
    }
  }
}

type ReserveChartOption = Record<string, unknown>

type ReserveChartInstance = {
  setOption: (option: ReserveChartOption, notMerge?: boolean) => void
  clear: () => void
  resize: () => void
  dispose: () => void
  on: (eventName: string, handler: (params: { dataIndex?: number }) => void) => void
  off: (eventName: string) => void
}

type PeriodOption = {
  label: string
  value: string
}

const props = defineProps<{
  assetStrength?: TraderDetailAssetStrength | null
  exchangeIcon?: string
  exchangeName?: string
  morePath?: string
  selectedFlowTimeRangeType?: string | number
  selectedVolumeTimeRangeType?: string | number
}>()

const emit = defineEmits<{
  (event: 'flow-period-change', value: string): void
  (event: 'volume-period-change', value: string): void
  (event: 'more'): void
}>()

const { t } = useAppI18n()
const { isRtl } = useRtl()
const reserveClassNames = ['btc', 'usdt', 'bnb', 'other', 'eth']
const reserveFallbackIcons: Record<string, string> = {
  BNB: BnbIcon,
  BTC: BtcIcon,
  ETH: EthIcon,
  USDT: UsdtIcon,
}

const RESERVE_LABEL_DEFAULT_COLOR = '#7d818a'
const RESERVE_BORDER_COLOR = '#ffffff'
const RESERVE_CHART_ANIMATION_DURATION = 860
const RESERVE_CHART_ANIMATION_DELAY_STEP = 90
const CHART_ANIMATION_DELAY_STEP = 26
const CHART_BASE_WIDTH = 368
const TWENTY_FOUR_HOUR_TIME_RANGE_TYPE = '5'
const EMPTY_TEXT = '--'
const selectedFlowPeriodValue = ref('5')
const selectedVolumePeriodValue = ref('5')
const isFlowPeriodOpen = ref(false)
const isVolumePeriodOpen = ref(false)
const activeReserveSliceIndex = ref(0)
const fundsSectionRef = ref<HTMLElement | null>(null)
const reserveChartRef = ref<HTMLDivElement | null>(null)
const isChartAnimationReady = ref(false)
let reserveChart: ReturnType<ReturnType<typeof useEcharts>['init']> | null = null
let reserveChartResizeObserver: ResizeObserver | null = null
let fundsAnimationObserver: IntersectionObserver | null = null
let hasPlayedReserveChartEntryAnimation = false
const chartTooltip = ref<ChartTooltipState>({
  visible: false,
  type: 'flow',
  index: -1,
  left: 0,
  top: 0,
  time: '',
  value: '',
  label: '',
})

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const compactMoneyUnitMultipliers: Record<string, number> = {
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  T: 1_000_000_000_000,
}

const parsePercentValue = (value: unknown) => {
  const parsed = Number.parseFloat(normalizeText(value).replace(/[%+,]/g, ''))

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

const normalizePointValue = (value: unknown) => {
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeScoreMax = (value: unknown) => {
  const parsed = Number(value)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : EMPTY_TEXT
}

const normalizeScore = (value: unknown, scoreMax: unknown) => {
  const parsed = Number(value)

  return Number.isFinite(parsed) && Number(normalizeScoreMax(scoreMax)) > 0 ? parsed : EMPTY_TEXT
}

const padTimePart = (value: number) => String(value).padStart(2, '0')

const parseChartTime = (value: string) => {
  const text = normalizeText(value)

  if (!text) {
    return null
  }

  if (/^\d{10,13}$/.test(text)) {
    const timestamp = Number(text)

    return new Date(text.length === 10 ? timestamp * 1000 : timestamp)
  }

  const normalizedText = text.replace(/\//g, '-')
  if (/^\d{1,2}:\d{1,2}(?::\d{1,2})?$/.test(normalizedText)) {
    const now = new Date()
    const [hour = '0', minute = '0', second = '0'] = normalizedText.split(':')

    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), Number(hour), Number(minute), Number(second))
  }

  const fullDateText = /^\d{1,2}-\d{1,2}/.test(normalizedText)
    ? `${new Date().getFullYear()}-${normalizedText}`
    : normalizedText
  const parsedDate = new Date(fullDateText.replace(' ', 'T'))

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const formatChartTime = (value: string, shouldShowTime: boolean) => {
  const text = normalizeText(value)
  const date = parseChartTime(text)

  if (!date) {
    return text || EMPTY_TEXT
  }

  const dateText = `${date.getFullYear()}/${padTimePart(date.getMonth() + 1)}/${padTimePart(date.getDate())}`

  if (!shouldShowTime) {
    return dateText
  }

  return `${dateText} ${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}:${padTimePart(date.getSeconds())}`
}

const getChartNumericPoints = (chart?: { points?: Array<number | string>; pointsVal?: number[] }) => {
  const pointValues = chart?.pointsVal?.map(normalizePointValue).filter((value) => Number.isFinite(value)) ?? []

  if (pointValues.length > 0) {
    return pointValues
  }

  return chart?.points?.map(normalizePointValue).filter((value) => Number.isFinite(value)) ?? []
}

const getChartDisplayPoint = (
  chart: { points?: Array<number | string>; pointsVal?: number[] } | undefined,
  index: number,
) => {
  const displayValue = normalizeText(chart?.points?.[index])

  return displayValue || EMPTY_TEXT
}

const getFractionDigits = (value: string) => {
  const [, decimals = ''] = value.split('.')

  return Math.min(decimals.length, 8)
}

const formatNumberTextWithThousands = (value: string) => {
  const normalizedValue = value.replace(/,/g, '')
  const parsed = Number(normalizedValue)

  if (!Number.isFinite(parsed)) {
    return value
  }

  const fractionDigits = getFractionDigits(normalizedValue)

  return parsed.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

const formatMoneyValue = (value: unknown) => {
  const text = normalizeText(value)

  if (!text) {
    return ''
  }

  const compactMatch = text.match(/^([-−]?)([$¥€£]?)([\d,]+(?:\.\d+)?)([KMBT])$/i)

  if (compactMatch) {
    const [, sign, currency, amount, unit] = compactMatch
    const parsedAmount = Number(amount.replace(/,/g, ''))
    const multiplier = compactMoneyUnitMultipliers[unit.toUpperCase()]

    if (Number.isFinite(parsedAmount) && multiplier) {
      const expandedAmount = parsedAmount * multiplier

      return `${sign === '−' ? '-' : sign}${currency}${expandedAmount.toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })}`
    }
  }

  const moneyMatch = text.match(/^([-−]?)([$¥€£]?)([\d,]+(?:\.\d+)?)(.*)$/)

  if (!moneyMatch) {
    return text
  }

  const [, sign, currency, amount, suffix] = moneyMatch

  return `${sign === '−' ? '-' : sign}${currency}${formatNumberTextWithThousands(amount)}${suffix}`
}

const formatUnitlessPrice = (value: unknown) => {
  const text = normalizeText(value)

  return text.replace(/^([-−]?)[$¥€£]\s*/, '$1').trim()
}

const normalizeTrendLabels = (labels?: string[]) => {
  return (labels ?? []).map(normalizeText).filter(Boolean)
}

const createLinePath = (points: Array<{ x: number; y: number }>) => {
  if (points.length === 0) {
    return ''
  }

  if (points.length === 1) {
    return `M${points[0].x} ${points[0].y}`
  }

  return points
    .map((point, index) => {
      if (index === 0) {
        return `M${point.x} ${point.y}`
      }

      return `L${point.x} ${point.y}`
    })
    .join(' ')
}

const createSmoothLinePath = (points: Array<{ x: number; y: number }>) => {
  if (points.length < 3) {
    return createLinePath(points)
  }

  const controlPointRatio = 0.18
  const [firstPoint] = points
  const commands = [`M${firstPoint.x} ${firstPoint.y}`]

  for (let index = 0; index < points.length - 1; index += 1) {
    const previousPoint = points[index - 1] ?? points[index]
    const currentPoint = points[index]
    const nextPoint = points[index + 1]
    const nextNextPoint = points[index + 2] ?? nextPoint
    const controlPointOne = {
      x: currentPoint.x + (nextPoint.x - previousPoint.x) * controlPointRatio,
      y: currentPoint.y + (nextPoint.y - previousPoint.y) * controlPointRatio,
    }
    const controlPointTwo = {
      x: nextPoint.x - (nextNextPoint.x - currentPoint.x) * controlPointRatio,
      y: nextPoint.y - (nextNextPoint.y - currentPoint.y) * controlPointRatio,
    }

    commands.push(
      `C${controlPointOne.x.toFixed(2)} ${controlPointOne.y.toFixed(2)} ${controlPointTwo.x.toFixed(2)} ${controlPointTwo.y.toFixed(2)} ${nextPoint.x} ${nextPoint.y}`,
    )
  }

  return commands.join(' ')
}

const assetStrength = computed(() => props.assetStrength ?? null)
const fundTags = computed(() => {
  const tags = assetStrength.value?.tags?.filter((tag) => normalizeText(tag.label) && normalizeText(tag.value)) ?? []

  return tags
})
const normalizePeriodOptions = (rawTimeRange: unknown) => {
  if (!rawTimeRange) {
    return []
  }

  if (Array.isArray(rawTimeRange)) {
    return rawTimeRange
      .map((item) => {
        if (typeof item !== 'object' || item === null) {
          const label = normalizeText(item)

          return label ? { label, value: label } : null
        }

        const option = item as { label?: unknown; value?: unknown }
        const label = normalizeText(option.label)
        const value = normalizeText(option.value)

        return label && value ? { label, value } : null
      })
      .filter((item): item is PeriodOption => item !== null)
  }

  return (
    (rawTimeRange as { options?: unknown[] } | undefined)?.options
      ?.map((item) => {
        const label = normalizeText(item)

        return label ? { label, value: label } : null
      })
      .filter((item): item is PeriodOption => item !== null) ?? []
  )
}
const periodOptions = computed<PeriodOption[]>(() => {
  const rawTimeRange = (assetStrength.value as { timeRange?: unknown } | null)?.timeRange

  return normalizePeriodOptions(rawTimeRange)
})
const flowPeriodOptions = computed<PeriodOption[]>(() => {
  const rawTimeRange = (assetStrength.value as { fundFlowTimeRange?: unknown; timeRange?: unknown } | null)
    ?.fundFlowTimeRange
  const options = normalizePeriodOptions(rawTimeRange)

  return options.length > 0 ? options : periodOptions.value
})
const volumePeriodOptions = computed<PeriodOption[]>(() => {
  const rawTimeRange = (assetStrength.value as { tradingVolumeTimeRange?: unknown; timeRange?: unknown } | null)
    ?.tradingVolumeTimeRange
  const options = normalizePeriodOptions(rawTimeRange)

  return options.length > 0 ? options : periodOptions.value
})
const selectedFlowPeriod = computed(
  () => flowPeriodOptions.value.find((option) => option.value === selectedFlowPeriodValue.value)?.label ?? EMPTY_TEXT,
)
const selectedVolumePeriod = computed(
  () =>
    volumePeriodOptions.value.find((option) => option.value === selectedVolumePeriodValue.value)?.label ?? EMPTY_TEXT,
)
const reserveSourceSlices = computed(() => {
  const slices = assetStrength.value?.assetReserve?.chart?.slices ?? []

  return slices.filter((slice) => normalizeText(slice.name) && parsePercentValue(slice.percent) > 0).slice(0, 5)
})
const reserveLabels = computed<ReserveLabel[]>(() => {
  if (reserveSourceSlices.value.length === 0) {
    return []
  }

  return reserveSourceSlices.value.map((slice, index) => {
    const name = normalizeText(slice.name)
    const className = reserveClassNames[index] ?? `reserve-${index}`

    return {
      icon: normalizeText(slice.icon) || reserveFallbackIcons[name.toUpperCase()] || OtherIcon,
      label: normalizeText(slice.percent),
      className,
      name,
      amount: normalizeText(slice.amount),
      color: normalizeText(slice.color) || '#8c8f97',
      value: parsePercentValue(slice.percent),
    }
  })
})
const reserveChartData = computed<ReserveChartData[]>(() =>
  reserveLabels.value.map((item) => ({
    ...item,
    itemStyle: {
      color: item.color,
      borderColor: RESERVE_BORDER_COLOR,
      borderWidth: 2,
    },
    labelLine: {
      lineStyle: {
        color: item.color,
      },
    },
  })),
)
const reserveChartSignature = computed(() =>
  reserveChartData.value.map((item) => [item.name, item.label, item.icon, item.color, item.value].join('|')).join(';'),
)
const reserveChartRich = computed<Record<string, Record<string, unknown>>>(() =>
  reserveChartData.value.reduce<Record<string, Record<string, unknown>>>(
    (rich, item, index) => ({
      ...rich,
      [`icon${index}`]: {
        width: 18,
        height: 18,
        align: 'center',
        verticalAlign: 'middle',
        backgroundColor: {
          image: item.icon,
        },
      },
    }),
    {
      label: {
        color: RESERVE_LABEL_DEFAULT_COLOR,
        fontSize: 12,
        fontWeight: 400,
        height: 18,
        lineHeight: 18,
        verticalAlign: 'middle',
      },
    },
  ),
)
const createReserveChartOption = (shouldAnimate: boolean): ReserveChartOption => ({
  animation: shouldAnimate && reserveChartData.value.length > 0,
  animationDuration: RESERVE_CHART_ANIMATION_DURATION,
  animationDurationUpdate: shouldAnimate ? RESERVE_CHART_ANIMATION_DURATION : 0,
  animationEasing: 'cubicOut',
  tooltip: {
    show: false,
  },
  series: [
    {
      type: 'pie',
      animationType: 'expansion',
      animationTypeUpdate: 'expansion',
      animationDelay: (index: number) => (shouldAnimate ? index * RESERVE_CHART_ANIMATION_DELAY_STEP : 0),
      clockwise: true,
      startAngle: 180,
      center: ['50%', '50%'],
      radius: [48, 78],
      avoidLabelOverlap: true,
      padAngle: 0.25,
      selectedOffset: 0,
      minShowLabelAngle: 1,
      labelLayout: {
        hideOverlap: false,
      },
      itemStyle: {
        borderColor: RESERVE_BORDER_COLOR,
        borderWidth: 1.5,
      },
      emphasis: {
        focus: 'self',
        blurScope: 'series',
        scale: true,
        scaleSize: 6,
        itemStyle: {
          borderColor: RESERVE_BORDER_COLOR,
          borderWidth: 1.5,
          opacity: 1,
        },
        label: {
          opacity: 1,
        },
        labelLine: {
          show: true,
          lineStyle: {
            width: 1,
            opacity: 1,
          },
        },
      },
      blur: {
        itemStyle: {
          opacity: 0.42,
        },
        label: {
          opacity: 0.46,
        },
        labelLine: {
          lineStyle: {
            opacity: 0.46,
          },
        },
      },
      label: {
        show: true,
        position: 'outside',
        alignTo: 'labelLine',
        distanceToLabelLine: 5,
        bleedMargin: 4,
        color: RESERVE_LABEL_DEFAULT_COLOR,
        fontFamily: 'PingFang SC, Outfit, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 18,
        overflow: 'none',
        formatter: (params: { data?: ReserveChartData; dataIndex: number }) => {
          const item = params.data

          return item ? `{icon${params.dataIndex}|} {label|${item.label}}` : ''
        },
        rich: reserveChartRich.value,
      },
      labelLine: {
        show: true,
        length: 14,
        length2: 24,
        smooth: false,
        minTurnAngle: 90,
        maxSurfaceAngle: 90,
        lineStyle: {
          width: 1,
          color: 'inherit',
        },
      },
      data: reserveChartData.value,
    },
  ],
})
const getEvenlySampledLabels = (labels: string[], targetCount = 7) => {
  if (labels.length <= targetCount) {
    return labels
  }

  const lastIndex = labels.length - 1

  return Array.from({ length: targetCount }, (_, index) => labels[Math.round((index * lastIndex) / (targetCount - 1))])
}
const timeline = computed(() => normalizeTrendLabels(assetStrength.value?.fundFlow?.chart?.labels))
const flowAxisTimeline = computed(() => getEvenlySampledLabels(timeline.value))
const inflowBars = computed(() => {
  const chart = assetStrength.value?.fundFlow?.chart
  const points = getChartNumericPoints(chart)

  if (points.length === 0) {
    return []
  }

  const values = points
  const maxValue = Math.max(...values.map((value) => Math.abs(value)), 1)
  const step = values.length > 1 ? (CHART_BASE_WIDTH - 6) / (values.length - 1) : 0

  return values.map((value, index) => {
    const h = Math.max(4, Math.round((Math.abs(value) / maxValue) * 72))
    const isOutflow = value < 0

    return {
      x: Math.round(index * step),
      h,
      type: isOutflow ? 'red' : 'green',
      top: isOutflow ? 104 : 104 - h,
      value: getChartDisplayPoint(chart, index),
    }
  })
})
const volumeTimeline = computed(() => normalizeTrendLabels(assetStrength.value?.tradingVolume?.chart?.labels))
const volumeAxisTimeline = computed(() => getEvenlySampledLabels(volumeTimeline.value))
const volumePoints = computed(() => {
  const chart = assetStrength.value?.tradingVolume?.chart
  const points = getChartNumericPoints(chart)

  if (points.length === 0) {
    return []
  }

  const values = points
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const range = maxValue - minValue || 1
  const step = values.length > 1 ? 368 / (values.length - 1) : 0

  return values.map((value, index) => ({
    x: Math.round(index * step),
    time: volumeTimeline.value[Math.min(index, volumeTimeline.value.length - 1)] ?? '',
    value: getChartDisplayPoint(chart, index),
    y: Math.round(154 - ((value - minValue) / range) * 130),
  }))
})
const volumeChartPoints = computed(() => {
  const points = getChartNumericPoints(assetStrength.value?.tradingVolume?.chart)

  if (points.length === 0) {
    return []
  }

  return volumePoints.value.map(({ x, y }) => ({ x, y }))
})
const volumeLinePath = computed(() => createSmoothLinePath(volumeChartPoints.value))
const volumeAreaPath = computed(() => (volumeLinePath.value ? `${volumeLinePath.value} L368 182 L0 182 Z` : ''))
const volumeKeyPoints = computed(() => {
  const numericPoints = getChartNumericPoints(assetStrength.value?.tradingVolume?.chart)

  if (numericPoints.length === 0) {
    return []
  }

  const minIndex = numericPoints.reduce(
    (minValueIndex, value, index) => (value < numericPoints[minValueIndex] ? index : minValueIndex),
    0,
  )
  const maxIndex = numericPoints.reduce(
    (maxValueIndex, value, index) => (value > numericPoints[maxValueIndex] ? index : maxValueIndex),
    0,
  )

  return Array.from(new Set([minIndex, maxIndex])).map((index) => {
    const { x, y } = volumePoints.value[index]

    return { x, y }
  })
})

const fundsTitle = computed(() => t('84441') || normalizeText(assetStrength.value?.title) || EMPTY_TEXT)
const coinIcon = computed(() => normalizeText(assetStrength.value?.summary?.icon))
const coinSymbol = computed(() => normalizeText(assetStrength.value?.summary?.symbol) || EMPTY_TEXT)
const coinPrice = computed(() => formatUnitlessPrice(assetStrength.value?.summary?.price) || EMPTY_TEXT)
const reserveTitle = computed(() => normalizeText(assetStrength.value?.assetReserve?.title) || EMPTY_TEXT)
const reserveScore = computed(() =>
  normalizeScore(assetStrength.value?.assetReserve?.score, assetStrength.value?.assetReserve?.scoreMax),
)
const reserveScoreMax = computed(() => normalizeScoreMax(assetStrength.value?.assetReserve?.scoreMax))
const reserveAmount = computed(() => formatMoneyValue(assetStrength.value?.assetReserve?.amount) || EMPTY_TEXT)
/**
 * 将接口返回的文案模板按占位符替换成最终展示文案。
 * @param text 文案模板，通常包含占位符。
 * @param replacements 需要替换的占位符和值。
 * @returns 可直接渲染的文案。
 */
const formatTemplateText = (text: unknown, replacements: Record<string, string>) => {
  const templateText = normalizeText(text)

  if (!templateText) {
    return EMPTY_TEXT
  }

  return Object.entries(replacements).reduce(
    (currentText, [placeholder, replacement]) => currentText.replaceAll(placeholder, replacement),
    templateText,
  )
}
const reserveCoveragePercent = computed(() => {
  const scoreValue = Number(reserveScore.value)

  if (!Number.isFinite(scoreValue)) {
    return EMPTY_TEXT
  }

  const coveragePercent = Math.round(100 - scoreValue)

  return `${Math.min(100, Math.max(0, coveragePercent))}%`
})
const reserveCoverageHtml = computed(() =>
  formatTemplateText(normalizeText(assetStrength.value?.assetReserve?.outperformPercentText) || t('84551', ['{0}']), {
    '{0}': `<span class="fund-p-span">${normalizeText(assetStrength.value?.assetReserve?.outperformPercentValue) || reserveCoveragePercent.value}</span>`,
  }),
)
const activeReserveSlice = computed(() => reserveLabels.value[activeReserveSliceIndex.value] ?? null)
const reserveCenterSymbol = computed(
  () =>
    activeReserveSlice.value?.name ||
    normalizeText(assetStrength.value?.assetReserve?.chart?.centerSymbol) ||
    EMPTY_TEXT,
)
const reserveCenterAmount = computed(
  () =>
    activeReserveSlice.value?.amount ||
    normalizeText(assetStrength.value?.assetReserve?.chart?.centerAmount) ||
    EMPTY_TEXT,
)
const reserveAriaLabel = computed(() =>
  reserveLabels.value
    .map((item) => `${item.name} ${item.label}`)
    .filter(Boolean)
    .join(', '),
)
const flowTitle = computed(() => normalizeText(assetStrength.value?.fundFlow?.title) || EMPTY_TEXT)
const flowScore = computed(() =>
  normalizeScore(assetStrength.value?.fundFlow?.score, assetStrength.value?.fundFlow?.scoreMax),
)
const flowAmount = computed(() => formatMoneyValue(assetStrength.value?.fundFlow?.amount) || EMPTY_TEXT)
const flowChangePercent = computed(() => normalizeText(assetStrength.value?.fundFlow?.changePercent) || EMPTY_TEXT)
const flowChangeDirection = computed(() => normalizeText(assetStrength.value?.fundFlow?.changeDirection))
const flowBadge = computed(() => normalizeText(assetStrength.value?.fundFlow?.badge) || EMPTY_TEXT)
const flowCoveragePercent = computed(() => {
  const scoreValue = Number(flowScore.value)

  if (!Number.isFinite(scoreValue)) {
    return EMPTY_TEXT
  }

  const coveragePercent = Math.round(100 - scoreValue)

  return `${Math.min(100, Math.max(0, coveragePercent))}%`
})
const flowCoverageHtml = computed(() =>
  formatTemplateText(normalizeText(assetStrength.value?.fundFlow?.outperformPercentText) || t('84552', ['{0}']), {
    '{0}': selectedFlowPeriod.value,
    '{1}': `<span class="fund-p-span">${normalizeText(assetStrength.value?.fundFlow?.outperformPercentValue) || flowCoveragePercent.value}</span>`,
  }),
)
const volumeTitle = computed(() => normalizeText(assetStrength.value?.tradingVolume?.title) || EMPTY_TEXT)
const volumeScore = computed(() =>
  normalizeScore(assetStrength.value?.tradingVolume?.score, assetStrength.value?.tradingVolume?.scoreMax),
)
const volumeScoreMax = computed(() => normalizeScoreMax(assetStrength.value?.tradingVolume?.scoreMax))
const volumeAmount = computed(() => formatMoneyValue(assetStrength.value?.tradingVolume?.amount) || EMPTY_TEXT)
const volumeChangePercent = computed(
  () => normalizeText(assetStrength.value?.tradingVolume?.changePercent) || EMPTY_TEXT,
)
const volumeChangeDirection = computed(() => normalizeText(assetStrength.value?.tradingVolume?.changeDirection))
const volumeCoveragePercent = computed(() => {
  const scoreValue = Number(volumeScore.value)

  if (!Number.isFinite(scoreValue)) {
    return EMPTY_TEXT
  }

  const coveragePercent = Math.round(100 - scoreValue)

  return `${Math.min(100, Math.max(0, coveragePercent))}%`
})
const volumeCoverageHtml = computed(() =>
  formatTemplateText(
    normalizeText(assetStrength.value?.tradingVolume?.outperformPercentText) || t('84553', ['{0}', '{1}']),
    {
      '{0}': selectedVolumePeriod.value,
      '{1}': `<span class="fund-p-span">${normalizeText(assetStrength.value?.tradingVolume?.outperformPercentValue) || volumeCoveragePercent.value}</span>`,
    },
  ),
)
const volumeChartColor = computed(() => (volumeChangeDirection.value === 'up' ? '#1f845a' : '#e2483d'))

watch(
  [() => props.selectedFlowTimeRangeType, flowPeriodOptions],
  ([value, flowOptions]) => {
    const nextPeriodValue = normalizeText(value)

    if (nextPeriodValue && flowOptions.some((option) => option.value === nextPeriodValue)) {
      selectedFlowPeriodValue.value = nextPeriodValue
    } else if (!flowOptions.some((option) => option.value === selectedFlowPeriodValue.value)) {
      selectedFlowPeriodValue.value = flowOptions[0]?.value ?? ''
    }
  },
  { immediate: true },
)

watch(
  [() => props.selectedVolumeTimeRangeType, volumePeriodOptions],
  ([value, volumeOptions]) => {
    const nextPeriodValue = normalizeText(value)

    if (nextPeriodValue && volumeOptions.some((option) => option.value === nextPeriodValue)) {
      selectedVolumePeriodValue.value = nextPeriodValue
    } else if (!volumeOptions.some((option) => option.value === selectedVolumePeriodValue.value)) {
      selectedVolumePeriodValue.value = volumeOptions[0]?.value ?? ''
    }
  },
  { immediate: true },
)

const closePeriodSelector = () => {
  isFlowPeriodOpen.value = false
  isVolumePeriodOpen.value = false
}

const toggleFlowPeriodSelector = () => {
  isFlowPeriodOpen.value = !isFlowPeriodOpen.value
  isVolumePeriodOpen.value = false
}

const toggleVolumePeriodSelector = () => {
  isVolumePeriodOpen.value = !isVolumePeriodOpen.value
  isFlowPeriodOpen.value = false
}

const selectPeriod = (period: PeriodOption, type: ChartTooltipType) => {
  if (type === 'flow') {
    selectedFlowPeriodValue.value = period.value
    emit('flow-period-change', period.value)
  } else {
    selectedVolumePeriodValue.value = period.value
    emit('volume-period-change', period.value)
  }

  closePeriodSelector()
}

const handleDocumentClick = (event: MouseEvent) => {
  if (!(event.target instanceof Element) || !event.target.closest('.period-selector')) {
    closePeriodSelector()
  }
}

const handleMoreClick = () => {
  trackAssetClick('更多')
  emit('more')
}

const resizeReserveChart = () => {
  reserveChart?.resize()
}

const setReserveChartOption = (shouldAnimate = false) => {
  if (!reserveChart) {
    return
  }

  reserveChart.clear()
  reserveChart.setOption(createReserveChartOption(shouldAnimate), true)
}

const handleReserveChartMouseover = (params: { dataIndex?: number }) => {
  const nextIndex = Number(params.dataIndex)

  if (Number.isInteger(nextIndex) && reserveLabels.value[nextIndex]) {
    activeReserveSliceIndex.value = nextIndex
  }
}

const handleReserveChartMouseout = () => {
  activeReserveSliceIndex.value = 0
}

const playReserveChartEntryAnimation = () => {
  if (
    hasPlayedReserveChartEntryAnimation ||
    !reserveChart ||
    !isChartAnimationReady.value ||
    reserveChartData.value.length === 0
  ) {
    return
  }

  hasPlayedReserveChartEntryAnimation = true
  setReserveChartOption(true)
}

const initReserveChart = async () => {
  await nextTick()

  if (!reserveChartRef.value || reserveChart) {
    return
  }

  const { init } = useEcharts()
  reserveChart = init(reserveChartRef.value, { lazy: true })

  void (async () => {
    const chart = await reserveChart?.ready()
    if (!chart) return
    chart.on('mouseover', handleReserveChartMouseover)
    chart.on('mouseout', handleReserveChartMouseout)
    setReserveChartOption()

    if (typeof ResizeObserver !== 'undefined') {
      reserveChartResizeObserver = new ResizeObserver(resizeReserveChart)
      reserveChartResizeObserver.observe(reserveChartRef.value)
    }
  })().catch(() => undefined)
}

watch(reserveChartSignature, () => {
  activeReserveSliceIndex.value = 0

  if (!isChartAnimationReady.value || hasPlayedReserveChartEntryAnimation) {
    setReserveChartOption()
    return
  }

  playReserveChartEntryAnimation()
})

watch(
  () => [props.selectedFlowTimeRangeType, props.selectedVolumeTimeRangeType],
  () => {
    hideChartTooltip()
  },
)

const getNearestIndex = (x: number, points: Array<{ x: number }>) => {
  return points.reduce((closestIndex, point, index) => {
    const closestDistance = Math.abs(points[closestIndex].x - x)
    const currentDistance = Math.abs(point.x - x)

    return currentDistance < closestDistance ? index : closestIndex
  }, 0)
}

const getFlowBarStyle = (bar: (typeof inflowBars.value)[number], index: number) => ({
  '--bar-delay': `${index * CHART_ANIMATION_DELAY_STEP}ms`,
  left: `${bar.x}px`,
  top: `${bar.top}px`,
  height: `${bar.h}px`,
})

const isFlowBarActive = (index: number) =>
  chartTooltip.value.visible && chartTooltip.value.type === 'flow' && chartTooltip.value.index === index

const getTimeAxisStyle = (labels: string[]) => ({
  gridTemplateColumns: `repeat(${Math.max(labels.length, 1)}, minmax(0, 1fr))`,
})

const isTwentyFourHourPeriod = (periodValue: string) => normalizeText(periodValue) === TWENTY_FOUR_HOUR_TIME_RANGE_TYPE

const getTooltipTime = (time: string, type: ChartTooltipType) => {
  const shouldShowTime =
    type === 'flow'
      ? isTwentyFourHourPeriod(selectedFlowPeriodValue.value)
      : isTwentyFourHourPeriod(selectedVolumePeriodValue.value)

  return formatChartTime(time, shouldShowTime)
}

const handleChartPointerMove = (event: PointerEvent, type: ChartTooltipType) => {
  const currentTarget = event.currentTarget as HTMLElement
  const rect = currentTarget.getBoundingClientRect()
  const relativeX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)

  if (type === 'flow') {
    const bars = inflowBars.value
    if (bars.length === 0) {
      hideChartTooltip()
      return
    }

    const labels = timeline.value
    const index = getNearestIndex(relativeX, bars)
    const bar = bars[index]
    const hour =
      labels[Math.min(index, labels.length - 1)] ||
      labels[Math.min(Math.floor((bar.x / CHART_BASE_WIDTH) * labels.length), labels.length - 1)]

    chartTooltip.value = {
      visible: true,
      type,
      index,
      left: bar.x + 3,
      top: 20,
      time: getTooltipTime(hour, type),
      value: bar.value,
      label: bar.type === 'red' ? '流出' : '流入',
    }
    return
  }

  const points = volumePoints.value
  if (points.length === 0) {
    hideChartTooltip()
    return
  }

  const index = getNearestIndex(relativeX, points)
  const point = points[index]

  chartTooltip.value = {
    visible: true,
    type,
    index,
    left: point.x,
    top: point.y + 24,
    time: getTooltipTime(point.time, type),
    value: point.value,
    label: t('82660'),
  }
}

const hideChartTooltip = () => {
  chartTooltip.value.visible = false
}

// 当卡片首次进入视口时启动图表入场动画。
const startChartAnimation = () => {
  isChartAnimationReady.value = true
  playReserveChartEntryAnimation()
  fundsAnimationObserver?.disconnect()
  fundsAnimationObserver = null
}

const setupReserveChartAnimation = async () => {
  await initReserveChart()

  if (!fundsSectionRef.value || typeof IntersectionObserver === 'undefined') {
    startChartAnimation()
    return
  }

  fundsAnimationObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting) {
        startChartAnimation()
      }
    },
    {
      root: null,
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.18,
    },
  )
  fundsAnimationObserver.observe(fundsSectionRef.value)
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  void setupReserveChartAnimation()
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  fundsAnimationObserver?.disconnect()
  fundsAnimationObserver = null
  reserveChartResizeObserver?.disconnect()
  reserveChartResizeObserver = null
  reserveChart?.off('mouseover')
  reserveChart?.off('mouseout')
  reserveChart?.dispose()
  reserveChart = null
})
</script>

<template>
  <section
    ref="fundsSectionRef"
    class="exchange-funds-section"
    :class="{ 'is-animation-ready': isChartAnimationReady }"
    aria-labelledby="exchange-funds-title"
  >
    <div class="funds-header">
      <div class="funds-title-wrap">
        <h2 id="exchange-funds-title" class="funds-title">{{ fundsTitle }}≈</h2>
        <div class="coin-pill">
          <span class="coin-name">
            <CommonImage v-if="coinIcon" :src="coinIcon" alt="" class="coin-icon" width="16" height="16" />
            {{ coinSymbol }}
          </span>
          <span class="coin-price">{{ coinPrice }}</span>
        </div>
      </div>
      <NuxtLink
        v-if="morePath"
        class="funds-more rtl:rotate-180"
        :to="morePath"
        target="_blank"
        @click="trackAssetClick('更多')"
      >
        <ArrowRight class="funds-more-icon" :class="{ 'funds-more-icon-rtl': isRtl }" aria-hidden="true" />
      </NuxtLink>
      <button v-else class="funds-more rtl:rotate-180" type="button" @click="handleMoreClick">
        <ArrowRight class="funds-more-icon" :class="{ 'funds-more-icon-rtl': isRtl }" aria-hidden="true" />
      </button>
    </div>

    <div class="funds-filter-row">
      <div class="funds-tags">
        <span v-for="tag in fundTags" :key="tag.label" class="funds-tag">
          <span class="tag-label">{{ tag.label }}</span>
          <strong class="tag-value">{{ tag.value }}</strong>
        </span>
      </div>
    </div>

    <div class="funds-chart-grid">
      <article class="fund-card reserve-card" aria-label="资产储备">
        <header class="fund-card-header">
          <h3>{{ reserveTitle }}</h3>
        </header>
        <div class="fund-p">
          <p class="fund-p-text" v-html="reserveCoverageHtml"></p>
          <div class="score">
            <span class="score-bookmark" aria-hidden="true">
              <svg class="score-bookmark-icon" viewBox="0 0 12 13.851" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.666667 0H11.3333C11.5101 0 11.6797 0.0702379 11.8047 0.195262C11.9298 0.320286 12 0.489856 12 0.666667V13.5173C12.0001 13.573 11.9862 13.6277 11.9597 13.6766C11.9332 13.7255 11.8948 13.767 11.8482 13.7973C11.8015 13.8275 11.748 13.8457 11.6926 13.85C11.6371 13.8543 11.5814 13.8447 11.5307 13.822L6 11.3533L0.469333 13.8213C0.418602 13.844 0.363008 13.8536 0.307607 13.8493C0.252206 13.8451 0.198756 13.827 0.152118 13.7968C0.10548 13.7666 0.0671335 13.7252 0.0405664 13.6764C0.0139994 13.6276 5.45506e-05 13.5729 0 13.5173V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0ZM6 7.66667L7.95933 8.69667L7.58533 6.51533L9.17 4.97L6.97933 4.65133L6 2.66667L5.02 4.65133L2.83 4.97L4.41467 6.51533L4.04133 8.69667L6 7.66667Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <strong>{{ reserveScore }}</strong
            ><span class="score-max"> /100</span>
          </div>
        </div>
        <div class="fund-value">{{ reserveAmount }}</div>
        <div class="reserve-visual" :aria-label="reserveAriaLabel">
          <div ref="reserveChartRef" class="reserve-echarts" aria-hidden="true"></div>
          <div class="donut-hole">
            <span>{{ reserveCenterSymbol }}</span>
            <strong>{{ reserveCenterAmount }}</strong>
          </div>
        </div>
      </article>

      <article class="fund-card flow-card" aria-label="资金流向">
        <header class="fund-card-header">
          <div class="fund-card-title-row">
            <h3>{{ flowTitle }}</h3>
            <div v-if="flowPeriodOptions.length > 0" class="period-selector">
              <button
                class="period-select"
                type="button"
                :aria-expanded="isFlowPeriodOpen"
                @click.stop="toggleFlowPeriodSelector"
              >
                {{ selectedFlowPeriod }}
                <svg
                  class="period-arrow"
                  viewBox="0 0 7 3.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M3.5 3.5L0 0H7L3.5 3.5Z" fill="currentColor" />
                </svg>
              </button>
              <div
                v-if="isFlowPeriodOpen"
                class="period-menu"
                role="listbox"
                :aria-activedescendant="`flow-period-${selectedFlowPeriodValue}`"
              >
                <button
                  v-for="period in flowPeriodOptions"
                  :id="`flow-period-${period.value}`"
                  :key="period.value"
                  type="button"
                  class="period-option"
                  :class="{ active: selectedFlowPeriodValue === period.value }"
                  role="option"
                  :aria-selected="selectedFlowPeriodValue === period.value"
                  @click.stop="selectPeriod(period, 'flow')"
                >
                  {{ period.label }}
                </button>
              </div>
            </div>
          </div>
        </header>
        <div class="fund-p">
          <p class="fund-p-text" v-html="flowCoverageHtml"></p>
          <div class="score">
            <span class="score-bookmark" aria-hidden="true">
              <svg class="score-bookmark-icon" viewBox="0 0 12 13.851" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.666667 0H11.3333C11.5101 0 11.6797 0.0702379 11.8047 0.195262C11.9298 0.320286 12 0.489856 12 0.666667V13.5173C12.0001 13.573 11.9862 13.6277 11.9597 13.6766C11.9332 13.7255 11.8948 13.767 11.8482 13.7973C11.8015 13.8275 11.748 13.8457 11.6926 13.85C11.6371 13.8543 11.5814 13.8447 11.5307 13.822L6 11.3533L0.469333 13.8213C0.418602 13.844 0.363008 13.8536 0.307607 13.8493C0.252206 13.8451 0.198756 13.827 0.152118 13.7968C0.10548 13.7666 0.0671335 13.7252 0.0405664 13.6764C0.0139994 13.6276 5.45506e-05 13.5729 0 13.5173V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0ZM6 7.66667L7.95933 8.69667L7.58533 6.51533L9.17 4.97L6.97933 4.65133L6 2.66667L5.02 4.65133L2.83 4.97L4.41467 6.51533L4.04133 8.69667L6 7.66667Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <strong>{{ flowScore }}</strong
            ><span class="score-max"> /100</span>
          </div>
        </div>
        <div class="flow-value-row">
          <div class="fund-value">{{ flowAmount }}</div>
          <span
            :class="{
              'positive-change': flowChangeDirection === 'up',
              'negative-change': flowChangeDirection === 'down',
            }"
          >
            {{ flowChangePercent }}
            <TrendDown
              v-if="flowChangeDirection === 'up' || flowChangeDirection === 'down'"
              class="trend-arrow"
              :class="flowChangeDirection"
              aria-hidden="true"
            />
          </span>
          <span class="net-badge" :class="{ outflow: flowChangeDirection === 'down' }">{{ flowBadge }}</span>
        </div>
        <div class="bar-chart" aria-label="资金流向柱状图">
          <div class="bar-plot" @pointermove="handleChartPointerMove($event, 'flow')" @pointerleave="hideChartTooltip">
            <div
              v-for="(bar, index) in inflowBars"
              :key="`${bar.x}-${bar.h}`"
              :class="[
                'flow-bar',
                bar.type,
                {
                  'is-active': isFlowBarActive(index),
                  'is-dim': chartTooltip.visible && chartTooltip.type === 'flow' && !isFlowBarActive(index),
                },
              ]"
              :style="getFlowBarStyle(bar, index)"
            ></div>
            <div
              v-if="chartTooltip.visible && chartTooltip.type === 'flow'"
              class="chart-guide-line"
              :style="{ left: `${chartTooltip.left}px` }"
              aria-hidden="true"
            ></div>
            <div
              v-if="chartTooltip.visible && chartTooltip.type === 'flow'"
              class="chart-tooltip flow-popover"
              :style="{ left: `${Math.min(chartTooltip.left + 12, 270)}px` }"
            >
              <strong>{{ chartTooltip.time }}</strong>
              <span>{{ chartTooltip.label }}: {{ chartTooltip.value }}</span>
            </div>
          </div>
          <div class="time-axis" :style="getTimeAxisStyle(flowAxisTimeline)">
            <span v-for="time in flowAxisTimeline" :key="time">{{ time }}</span>
          </div>
        </div>
      </article>

      <article class="fund-card volume-card" aria-label="交易量">
        <header class="fund-card-header">
          <div class="fund-card-title-row">
            <h3>{{ volumeTitle }}</h3>
            <div v-if="volumePeriodOptions.length > 0" class="period-selector">
              <button
                class="period-select"
                type="button"
                aria-label="选择交易量时间范围"
                :aria-expanded="isVolumePeriodOpen"
                @click.stop="toggleVolumePeriodSelector"
              >
                {{ selectedVolumePeriod }}
                <svg
                  class="period-arrow"
                  viewBox="0 0 7 3.5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M3.5 3.5L0 0H7L3.5 3.5Z" fill="currentColor" />
                </svg>
              </button>
              <div
                v-if="isVolumePeriodOpen"
                class="period-menu"
                role="listbox"
                :aria-activedescendant="`volume-period-${selectedVolumePeriodValue}`"
              >
                <button
                  v-for="period in volumePeriodOptions"
                  :id="`volume-period-${period.value}`"
                  :key="period.value"
                  type="button"
                  class="period-option"
                  :class="{ active: selectedVolumePeriodValue === period.value }"
                  role="option"
                  :aria-selected="selectedVolumePeriodValue === period.value"
                  @click.stop="selectPeriod(period, 'volume')"
                >
                  {{ period.label }}
                </button>
              </div>
            </div>
          </div>
        </header>
        <div class="fund-p">
          <p class="fund-p-text" v-html="volumeCoverageHtml"></p>
          <div class="score">
            <span class="score-bookmark" aria-hidden="true">
              <svg class="score-bookmark-icon" viewBox="0 0 12 13.851" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.666667 0H11.3333C11.5101 0 11.6797 0.0702379 11.8047 0.195262C11.9298 0.320286 12 0.489856 12 0.666667V13.5173C12.0001 13.573 11.9862 13.6277 11.9597 13.6766C11.9332 13.7255 11.8948 13.767 11.8482 13.7973C11.8015 13.8275 11.748 13.8457 11.6926 13.85C11.6371 13.8543 11.5814 13.8447 11.5307 13.822L6 11.3533L0.469333 13.8213C0.418602 13.844 0.363008 13.8536 0.307607 13.8493C0.252206 13.8451 0.198756 13.827 0.152118 13.7968C0.10548 13.7666 0.0671335 13.7252 0.0405664 13.6764C0.0139994 13.6276 5.45506e-05 13.5729 0 13.5173V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0ZM6 7.66667L7.95933 8.69667L7.58533 6.51533L9.17 4.97L6.97933 4.65133L6 2.66667L5.02 4.65133L2.83 4.97L4.41467 6.51533L4.04133 8.69667L6 7.66667Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <strong>{{ volumeScore }}</strong
            ><span class="score-max"> /100</span>
          </div>
        </div>
        <div class="flow-value-row">
          <div class="fund-value">{{ volumeAmount }}</div>
          <span
            :class="{
              'positive-change': volumeChangeDirection === 'up',
              'negative-change': volumeChangeDirection === 'down',
            }"
          >
            {{ volumeChangePercent }}
            <TrendDown
              v-if="volumeChangeDirection === 'up' || volumeChangeDirection === 'down'"
              class="trend-arrow"
              :class="volumeChangeDirection"
              aria-hidden="true"
            />
          </span>
        </div>
        <div
          class="line-chart"
          aria-label="24H 交易量走势"
          :style="{ '--volume-chart-color': volumeChartColor }"
          @pointermove="handleChartPointerMove($event, 'volume')"
          @pointerleave="hideChartTooltip"
        >
          <svg viewBox="0 0 368 182" width="368" height="182" aria-hidden="true">
            <defs>
              <linearGradient id="volumeArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stop-color="var(--volume-chart-color)" stop-opacity="0.08" />
                <stop offset="72%" stop-color="var(--volume-chart-color)" stop-opacity="0.025" />
                <stop offset="100%" stop-color="var(--volume-chart-color)" stop-opacity="0" />
              </linearGradient>
            </defs>
            <path class="volume-area" :d="volumeAreaPath" fill="url(#volumeArea)" />
            <path
              class="volume-line"
              :d="volumeLinePath"
              fill="none"
              pathLength="1"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
            <circle
              v-for="point in volumeKeyPoints"
              :key="`${point.x}-${point.y}`"
              class="volume-key-dot"
              :cx="point.x"
              :cy="point.y"
              r="4"
            />
          </svg>
          <div
            v-if="chartTooltip.visible && chartTooltip.type === 'volume'"
            class="chart-guide-line volume-guide"
            :style="{ left: `${chartTooltip.left}px` }"
            aria-hidden="true"
          ></div>
          <div
            v-if="chartTooltip.visible && chartTooltip.type === 'volume'"
            class="volume-hover-dot"
            :style="{ left: `${chartTooltip.left}px`, top: `${chartTooltip.top}px` }"
            aria-hidden="true"
          ></div>
          <div
            v-if="chartTooltip.visible && chartTooltip.type === 'volume'"
            class="chart-tooltip volume-popover"
            :style="{
              left: `${Math.min(chartTooltip.left + 12, 230)}px`,
              top: `${Math.max(chartTooltip.top - 64, 24)}px`,
            }"
          >
            <strong>{{ chartTooltip.time }}</strong>
            <span>{{ chartTooltip.label }}: {{ chartTooltip.value }}</span>
          </div>
          <div class="time-axis" :style="getTimeAxisStyle(volumeAxisTimeline)">
            <span v-for="time in volumeAxisTimeline" :key="time">{{ time }}</span>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-funds-section {
  width: 100%;
  padding: 24px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #ffffff;
  color: #292a2e;
}

.funds-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding-bottom: 16px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
}

.funds-title-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
}

.funds-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
}

.coin-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 4px 12px;
  border-radius: 1000px;
  background: #f8f8f8;
}

.coin-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #505258;
  font-size: 14px;
  line-height: 22px;
}

.coin-icon {
  border-radius: 50%;
}

.coin-price {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}

.funds-more {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 2px;
  color: #7d818a;
  background: transparent;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}
.funds-more :hover {
  color: #292a2e;
}

.funds-more-icon {
  width: 20px;
  height: 20px;
  &:hover {
    color: #292a2e;
  }
}

.funds-more-icon-rtl {
  transform: rotate(180deg);
}

.funds-more-icon :deep(path) {
  fill: currentColor;
}

.funds-filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  margin-top: 20px;
}

.funds-tags {
  display: flex;
  align-items: center;
  gap: 20px;
}

.funds-tag,
.period-select {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 30px;
  padding: 4px 8px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 2px;
  background: #ffffff;
  font-size: 14px;
  line-height: 22px;
}

.tag-label {
  color: #505258;
  font-weight: 400;
}

.tag-value {
  color: #292a2e;
  font-weight: 500;
}

.period-selector {
  position: relative;
  z-index: 5;
}

.period-select {
  min-width: 68px;
  padding: 4px 12px;
  color: #292a2e;
  cursor: pointer;
}

.period-arrow {
  width: 7px;
  height: 3.5px;
  color: #7d818a;
  transition: transform 0.2s ease;
}

.period-select[aria-expanded='true'] .period-arrow {
  transform: rotate(180deg);
}

.period-menu {
  position: absolute;
  top: 40px;
  right: 0;
  display: flex;
  width: 100px;
  flex-direction: column;
  overflow: hidden;
  border-radius: 6px;
  background: #ffffff;
  padding: 4px 0;
  box-shadow:
    0 0 1px rgba(30, 31, 33, 0.12),
    0 8px 20px rgba(30, 31, 33, 0.16);
}

.period-option {
  height: 32px;
  padding: 0 12px;
  color: #292a2e;
  background: #ffffff;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.period-option.active {
  color: #4688ec;
}

.period-option:hover {
  background: #f8f8f8;
}

.funds-chart-grid {
  display: grid;
  grid-template-columns: repeat(3, 368px);
  gap: 24px;
  margin-top: 20px;
}

.fund-card {
  width: 368px;
  border-radius: 8px;
  background: #ffffff;
}

.fund-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
}

.fund-card-header h3 {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.fund-card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.fund-card-title-row .period-select {
  height: 24px;
  min-width: 56px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 18px;
}

.fund-card-title-row .period-menu {
  top: 30px;
}

.score {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  line-height: 20px;
  color: #7d818a;
}

.score strong {
  color: #292a2e;
  font-size: 16px;
  line-height: 24px;
}

.score-max {
  margin-left: 2px;
}

.score-bookmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #292a2e;
}

.score-bookmark-icon {
  width: 12px;
  height: 14px;
}

.fund-p {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;

  .fund-p-text {
    font-size: 13px;
    color: #8c8f97;
  }

  :deep(.fund-p-span) {
    color: #e2483d;
  }
}

.fund-value {
  padding: 14px 0 12px;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  white-space: nowrap;
}

.reserve-visual {
  position: relative;
  height: 224px;
}

.reserve-echarts {
  position: absolute;
  inset: 0;
  width: 368px;
  height: 224px;
  transform-origin: 50% 50%;
}

.exchange-funds-section.is-animation-ready .reserve-echarts {
  animation: reserve-chart-enter 1100ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.donut-hole {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.exchange-funds-section.is-animation-ready .donut-hole {
  animation: donut-hole-enter 760ms cubic-bezier(0.22, 1, 0.36, 1) 180ms both;
}

.donut-hole span {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.donut-hole strong {
  color: #7d818a;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

@keyframes reserve-chart-enter {
  0% {
    opacity: 0;
    transform: scale(0.78) rotate(-8deg);
  }

  58% {
    opacity: 1;
  }

  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes donut-hole-enter {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.82);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.flow-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding-top: 16px;
  padding-bottom: 12px;
}

.flow-value-row .fund-value {
  padding: 0;
}

.positive-change,
.negative-change {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
}

.positive-change {
  color: #1f845a;
}

.negative-change {
  color: #e2483d;
}

.trend-arrow {
  --trend-arrow-rotate: 0deg;
  --trend-arrow-shift: 2px;

  display: inline-flex;
  width: 14px;
  height: 14px;
  margin-left: 0;
  color: currentColor;
  vertical-align: -2px;
  animation: trend-arrow-bounce 1.05s ease-in-out infinite;
}

.trend-arrow :deep(path) {
  fill: currentColor;
}

.trend-arrow.up {
  --trend-arrow-rotate: 180deg;
  --trend-arrow-shift: -2px;
  transform: rotate(var(--trend-arrow-rotate));
}

.trend-arrow.down {
  transform: rotate(var(--trend-arrow-rotate));
}

.net-badge {
  height: 20px;
  padding: 0 8px;
  border: 0.5px solid rgba(34, 160, 107, 0.24);
  border-radius: 2px;
  background: #effcf5;
  color: #1f845a;
  font-size: 12px;
  line-height: 19px;
}

.net-badge.outflow {
  border-color: rgba(226, 72, 61, 0.24);
  background: #fff4f2;
  color: #e2483d;
}

.bar-chart,
.line-chart {
  --chart-axis-y: 206px;

  position: relative;
  height: 224px;
}

.bar-plot {
  position: absolute;
  top: 0;
  left: 0;
  width: 368px;
  height: 206px;
  cursor: crosshair;
}

.chart-guide-line {
  position: absolute;
  top: 0;
  height: 206px;
  border-left: 0.5px dashed rgba(5, 21, 36, 0.16);
  pointer-events: none;
}

.flow-bar {
  position: absolute;
  width: 6px;
  border-radius: 0;
  transform-origin: center bottom;
  transition:
    opacity 0.16s ease,
    width 0.16s ease,
    margin-left 0.16s ease,
    box-shadow 0.16s ease;
  animation: flowBarIn 0.58s cubic-bezier(0.2, 0.9, 0.2, 1) var(--bar-delay, 0ms) both;
  animation-play-state: paused;
}

.flow-bar.green {
  background: #22a06b;
}

.flow-bar.red {
  background: #e2483d;
  transform-origin: center top;
}

.flow-bar.is-active {
  z-index: 2;
  width: 8px;
  margin-left: -1px;
  box-shadow: 0 6px 14px rgba(34, 160, 107, 0.24);
}

.flow-bar.red.is-active {
  box-shadow: 0 6px 14px rgba(226, 72, 61, 0.24);
}

.flow-bar.is-dim {
  opacity: 0.36;
}

.chart-tooltip {
  position: absolute;
  z-index: 4;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 85px;
  min-height: 56px;
  padding: 8px 12px;
  border-radius: 4px;
  background: rgba(5, 12, 31, 0.46);
  color: #ffffff;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  pointer-events: none;
  animation: chartTooltipIn 0.16s ease both;
}

.chart-tooltip strong {
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
}

.time-axis {
  position: absolute;
  left: 0;
  top: var(--chart-axis-y);
  display: grid;
  width: 368px;
  height: 18px;
  color: #7d818a;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}

.time-axis span {
  min-width: 0;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.bar-chart .time-axis::before,
.line-chart .time-axis::before {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border-top: 1px dashed rgba(125, 129, 138, 0.28);
  content: '';
}

.line-chart svg {
  position: absolute;
  top: 24px;
  left: 0;
  pointer-events: none;
}

.line-chart {
  cursor: crosshair;
}

.volume-line {
  stroke: var(--volume-chart-color);
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: volumeLineIn 1.12s cubic-bezier(0.25, 1, 0.5, 1) 0.12s forwards;
  animation-play-state: paused;
}

.volume-area {
  opacity: 0;
  transform-origin: center bottom;
  animation: volumeAreaIn 0.85s ease 0.5s forwards;
  animation-play-state: paused;
}

.volume-key-dot {
  fill: var(--volume-chart-color);
  stroke: #ffffff;
  stroke-width: 2px;
  opacity: 0;
  animation: volumeAreaIn 0.3s ease 0.9s forwards;
  animation-play-state: paused;
}

.volume-guide {
  top: 24px;
  height: 182px;
}

.volume-hover-dot {
  position: absolute;
  z-index: 2;
  width: 8px;
  height: 8px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: var(--volume-chart-color);
  box-shadow: 0 0 0 1px var(--volume-chart-color);
  pointer-events: none;
  transform: translate(-50%, -50%);
  animation: volumeDotPulse 1.2s ease infinite;
  animation-play-state: paused;
}

.exchange-funds-section.is-animation-ready {
  .flow-bar,
  .volume-line,
  .volume-area,
  .volume-key-dot,
  .volume-hover-dot {
    animation-play-state: running;
  }
}

.volume-popover {
  top: 42px;
  min-width: 150px;
}

@keyframes reserveSegmentIn {
  to {
    opacity: 1;
    stroke-dashoffset: 0;
  }
}

@keyframes flowBarIn {
  from {
    opacity: 0;
    transform: scaleY(0);
  }

  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

@keyframes chartTooltipIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes trend-arrow-bounce {
  0%,
  100% {
    transform: translateY(0) rotate(var(--trend-arrow-rotate));
  }

  50% {
    transform: translateY(var(--trend-arrow-shift)) rotate(var(--trend-arrow-rotate));
  }
}

@keyframes volumeLineIn {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes volumeAreaIn {
  from {
    opacity: 0;
    transform: scaleY(0.65);
  }

  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

@keyframes volumeDotPulse {
  0%,
  100% {
    box-shadow:
      0 0 0 1px #e2483d,
      0 0 0 0 rgba(226, 72, 61, 0.24);
  }

  50% {
    box-shadow:
      0 0 0 1px #e2483d,
      0 0 0 6px rgba(226, 72, 61, 0);
  }
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .exchange-funds-section {
    height: auto;
  }

  .funds-filter-row {
    height: auto;
    align-items: flex-start;
    gap: 16px;
  }

  .funds-tags {
    flex-wrap: wrap;
  }

  .funds-chart-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .fund-card {
    margin: 0 auto;
  }
}
</style>
